import { useCallback, useEffect, useState } from "react";
import loader from "../../../../../components/recharts/loader.gif";
import SimulationVolumeAnalysis from "./SystemChart/SimulationVolumeChart";
import SimulationCostAnalysis from "./SystemChart/SimulationCostChart";
import SimulationCumulatedVolumeAnalysis from "./SummaryChart/SimulationSummaryVolume";
import SimulationCumulatedCostAnalysis from "./SummaryChart/SimulationSummaryCost";
import {
  fetchProcurementSummaryData,
  fetchSimulationProcurementData,
} from "../../../../../Rest_api/DemoRestApi";
import { getMonthFromDate } from "../../../../../extensions/date";
const chartTabs = ["Summary", "Detailed"];
interface DeficitSlot {
  slot: string;
  timeRange: string;
  deficitMW: number;
}

interface SurplusSlot {
  slot: string;
  timeRange: string;
  surplusMW: number;
}

export default function SurplusDeficitAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  setUniqueTargetDate,
  selectedTargetDate,
  activeTab,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  setUniqueTargetDate?: any;
  selectedTargetDate: any[];
  activeTab: string;
}) {
  const [loading, setLoading] = useState(false);
  const [activeChartTab, setActiveChartTab] = useState(chartTabs[0]);
  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [detailedData, setDetailedData] = useState<any[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<string[]>([
    "surplus",
    "deficit",
  ]);
  const [selectedSimulatedOptions, setSelectedSimulatedOptions] = useState<
    string[]
  >(["simulated", "optimal"]);
  const handleFetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [summaryResponse, detailedResponse] = await Promise.all([
        fetchProcurementSummaryData(),
        fetchSimulationProcurementData(),
      ]);
      let dataArray = Array.isArray(summaryResponse)
        ? summaryResponse
        : summaryResponse?.data || [];

      if (!Array.isArray(dataArray)) {
        setError?.("Invalid data format: expected array.");
        setSummaryData([]);
        // return; // Don't return here, try to process detailed data too or handle error appropriately. If summary fails, maybe detailed works?
        // But the original code returned. Let's keep it safe.
      } else {
        // Clean and validate data
        const cleanedData = dataArray.map((item) => ({
          ...item,
          cummulated_vol: Number(item.cummulated_vol) || 0,
          cummulated_cost: Number(item.cummulated_cost) || 0,
          slot: Number(item.slot) || 0,
          month: getMonthFromDate(item.date),
        }));

        if (setUniqueTargetDate && cleanedData.length > 0) {
          const uniqueDates = Array.from(
            new Set(
              cleanedData
                .filter((item) => item.date)
                .map((item: { date: string }) =>
                  item.date.split("-").reverse().join("-"),
                ),
            ),
          );
          setUniqueTargetDate(uniqueDates);
        }

        setSummaryData(cleanedData);
      }

      // Process detailed data
      let detailedArray = Array.isArray(detailedResponse)
        ? detailedResponse
        : detailedResponse?.data || [];
      if (!Array.isArray(detailedArray)) {
        setDetailedData([]);
      } else {
        setDetailedData(detailedArray);
      }
    } catch (error) {
      console.error("Error fetching data", error);
      setError?.("An error occurred while fetching data.");
      setSummaryData([]);
      setDetailedData([]);
    } finally {
      setLoading(false);
    }
  }, [setError, setUniqueTargetDate]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);
  return (
    <div className="w-full h-full flex gap-2 min-w-0">
      {/* System Balance Chart - 70% width */}
      <div
        className="h-full min-w-0 !shadow-[rgba(149,157,165,0.2)_0px_8px_24px]"
        style={{ width: "100%", flex: "0 0 100%" }}
      >
        {loading ? (
          <div className="w-full h-full bg-white flex items-center justify-center rounded-2xl border border-[#E5E7EB]">
            <img src={loader} width={200} alt="loader" className="block" />
          </div>
        ) : (
          <div className="w-full h-full min-w-0 overflow-hidden">
            {activeTab === "Volume" && (
              <SimulationVolumeAnalysis
                selectedBlock={selectedBlock}
                refreshKey={refreshKey}
                setError={setError}
                setUniqueTargetDate={setUniqueTargetDate}
                selectedTargetDate={selectedTargetDate}
                activeChartTab={activeChartTab}
                setActiveChartTab={setActiveChartTab}
                tab={chartTabs}
                summaryData={summaryData}
                detailedData={detailedData}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                selectedSimulatedOptions={selectedSimulatedOptions}
                setSelectedSimulatedOptions={setSelectedSimulatedOptions}
              />
            )}
            {activeTab === "Cost" && (
              <SimulationCostAnalysis
                selectedBlock={selectedBlock}
                refreshKey={refreshKey}
                setError={setError}
                setUniqueTargetDate={setUniqueTargetDate}
                selectedTargetDate={selectedTargetDate}
                activeChartTab={activeChartTab}
                setActiveChartTab={setActiveChartTab}
                tab={chartTabs}
                summaryData={summaryData}
                detailedData={detailedData}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                selectedSimulatedOptions={selectedSimulatedOptions}
                setSelectedSimulatedOptions={setSelectedSimulatedOptions}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
